import React, { useState, useEffect } from 'react';
import { Table, Form, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { withAuth } from '../../api/withAuth';

const GoldFinderApi = withAuth(axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_API_URL
}));

const CorrectedANumbersTable = ({ onANumberClick, refreshTrigger }) => {
    const [anumbers, setAnumbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: 'last_updated',
        direction: 'desc'
    });

    useEffect(() => {
        fetchAnumbers();
    }, [refreshTrigger]);

    const fetchAnumbers = async () => {
        try {
            setLoading(true);
            const response = await GoldFinderApi.get('/admin/soil-corrections/anumbers');
            setAnumbers(response.data.anumbers);
            setError(null);
        } catch (error) {
            console.error('Error fetching corrected ANumbers:', error);
            setError('Failed to load corrected ANumbers');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return '↕️';
        }
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString() + ' ' + 
               new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const filteredAnumbers = anumbers.filter(anumber => 
        !filter || anumber.anumber.toString().toLowerCase().includes(filter.toLowerCase())
    );

    const sortedAnumbers = [...filteredAnumbers].sort((a, b) => {
        if (!sortConfig.key) return 0;

        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        // Handle different data types
        if (sortConfig.key === 'correction_count') {
            aValue = Number(aValue) || 0;
            bValue = Number(bValue) || 0;
        } else if (sortConfig.key === 'last_updated') {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
        } else if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });

    if (loading) {
        return (
            <div className="text-center p-3">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading corrected ANumbers...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger">
                {error}
            </Alert>
        );
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>ANumbers with Corrections ({anumbers.length})</h5>
                <Form.Control
                    type="text"
                    placeholder="Filter by ANumber..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    style={{ width: '250px' }}
                    size="sm"
                />
            </div>

            {anumbers.length === 0 ? (
                <Alert variant="info">
                    No corrected ANumbers found. Import some corrections to see them here.
                </Alert>
            ) : (
                <div style={{ 
                    maxHeight: 'calc(100vh - 400px)', 
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.375rem'
                }}>
                    <Table striped bordered hover style={{ marginBottom: 0 }}>
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                            <tr>
                                <th 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('anumber')}
                                >
                                    ANumber {getSortIcon('anumber')}
                                </th>
                                <th 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('correction_count')}
                                >
                                    Corrections {getSortIcon('correction_count')}
                                </th>
                                <th 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('last_updated')}
                                >
                                    Last Updated {getSortIcon('last_updated')}
                                </th>
                                <th 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('last_updated_by')}
                                >
                                    Last Updated By {getSortIcon('last_updated_by')}
                                </th>
                                <th style={{ width: '80px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedAnumbers.map((anumber, index) => (
                                <tr 
                                    key={anumber.anumber}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => onANumberClick(anumber.anumber)}
                                    className="table-row-hover"
                                >
                                    <td>
                                        <strong>{anumber.anumber}</strong>
                                    </td>
                                    <td>
                                        <Badge bg="primary">
                                            {anumber.correction_count}
                                        </Badge>
                                    </td>
                                    <td>
                                        {formatDate(anumber.last_updated)}
                                    </td>
                                    <td>
                                        {anumber.last_updated_by || 'N/A'}
                                    </td>
                                    <td>
                                        <FontAwesomeIcon 
                                            icon={faEye} 
                                            className="text-primary"
                                            title="View details"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* Add some custom CSS for hover effect */}
            <style jsx>{`
                .table-row-hover:hover {
                    background-color: #f8f9fa !important;
                }
            `}</style>
        </>
    );
};

export default CorrectedANumbersTable;